import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { categoryColors } from "@plasma/data/categories";
import { daysOfTheWeek, timeOptions } from "@plasma/data/time";
import { timeConvert24hrTo12hr } from "@plasma/utils/time-convert";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import {
  categoriesCloneAndUpdate,
  categoriesGetById,
  categoriesUpdate,
} from "~/api-refs";
import { categoryCardColors } from "~/components/menus/category-card-colors";
import { SharedCategoryDialog } from "~/components/menus/shared-category-dialog";
import {
  AvailabilityTimesFields,
  type AvailabilityTimesForm,
} from "~/components/shared/availability-times-fields";
import { ColorSelector } from "~/components/shared/color-selector";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/categories/$categoryId/edit"
)({
  component: EditCategoryModal,
});

// Full form data type matching the old implementation
interface CategoryFormData extends AvailabilityTimesForm {
  name: string;
  color: (typeof categoryColors)[number];
  isCategoryLockEnabled: boolean;
  matchBusinessHours: boolean;
}

const isCategoryColor = enumIncludes(categoryColors);
const isDayOfWeek = enumIncludes(daysOfTheWeek);
const isTimeOption = enumIncludes(timeOptions);

function EditCategoryModal() {
  const { menuId, categoryId: categoryIdParam } = Route.useParams();
  const categoryId = toConvexTableId("categories", categoryIdParam);
  const navigate = useNavigate();

  // State for shared category workflow
  const savedFormData = useRef<CategoryFormData | null>(null);
  const [updateSharedCategoryStatus, setUpdateSharedCategoryStatus] = useState<
    "confirm" | "select-menus" | null
  >(null);
  const [menusToCopyCategoryTo, setMenusToCopyCategoryTo] = useState<string[]>(
    []
  );

  // Fetch category data with all relations needed for shared category detection
  const { data: category, isPending } = useQuery(
    convexQuery(categoriesGetById, {
      id: toConvexTableId("categories", categoryId),
      includeAvailabilityTimes: true,
      includeMenus: true,
      includeLocations: true,
      includeItemsCount: true,
    })
  );

  // Update category mutation (updates all shared menus)
  const updateCategoryMutation = useMutation({
    mutationFn: (params: {
      id: Id<"categories">;
      name?: string;
      color?: string;
      matchBusinessHours?: boolean;
      isCategoryLockEnabled?: boolean;
    }) => {
      return convexClient.mutation(categoriesUpdate, params);
    },
    onSuccess: () => {
      ProductEvents.categoryUpdated(categoryId);
    },
  });

  // Clone and update mutation (creates new category for selected menus)
  const cloneAndUpdateCategoryMutation = useMutation({
    mutationFn: (params: {
      id: Id<"categories">;
      data: {
        name: string;
        color: string;
        isCategoryLockEnabled: boolean;
        matchBusinessHours: boolean;
        availabilityTimes: {
          dayOfTheWeek: string;
          startTime: string;
          endTime: string;
        }[];
      };
      menuIds: Id<"menus">[];
    }) => convexClient.mutation(categoriesCloneAndUpdate, params),
  });

  // Calculate unique locations affected by this shared category
  const uniqueLocations = useMemo(() => {
    if (!category?.menuRelations) {
      return [];
    }

    const locations: {
      id: string;
      name: string;
      logoUrl: string | null;
    }[] = [];

    for (const menuRel of category.menuRelations) {
      const menuWithLocations = menuRel.menu;
      if (
        "locationRelations" in menuWithLocations &&
        menuWithLocations.locationRelations
      ) {
        for (const locationRel of menuWithLocations.locationRelations) {
          if (!locations.find((r) => r.id === locationRel.location.id)) {
            locations.push({
              id: locationRel.location.id,
              name: locationRel.location.name,
              logoUrl: locationRel.location.logoUrl ?? null,
            });
          }
        }
      }
    }

    return locations;
  }, [category]);

  const form = useForm<CategoryFormData>({
    defaultValues: {
      name: "",
      color: "gray",
      matchBusinessHours: true,
      availabilityTimes: [],
      isCategoryLockEnabled: false,
    },
  });

  // Populate form when data loads
  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name,
        color: parseEnumValue(category.color, isCategoryColor, "gray"),
        matchBusinessHours: category.matchBusinessHours,
        isCategoryLockEnabled: category.isCategoryLockEnabled,
        availabilityTimes: category.availabilityTimes.map(
          (availabilityTime) => ({
            dayOfTheWeek: parseEnumValue(
              availabilityTime.dayOfTheWeek,
              isDayOfWeek,
              "Monday"
            ),
            endTime: parseEnumValue(
              timeConvert24hrTo12hr(availabilityTime.endTime),
              isTimeOption,
              "12:00 AM"
            ),
            startTime: parseEnumValue(
              timeConvert24hrTo12hr(availabilityTime.startTime),
              isTimeOption,
              "12:00 AM"
            ),
          })
        ),
      });
    }
  }, [category, form]);

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/categories/${categoryId}` });
  };

  const handleNavigateToDelete = () => {
    void navigate({ to: `/menus/${menuId}/categories/${categoryId}/delete` });
  };

  const handleSave = async (formData: CategoryFormData) => {
    if (updateCategoryMutation.isPending) {
      return;
    }

    // Check if category is shared across multiple menus
    if (category?.menuRelations && category.menuRelations.length > 1) {
      // Save form data and show confirmation dialog
      savedFormData.current = formData;
      setUpdateSharedCategoryStatus("confirm");
      return;
    }

    // Single menu - just update directly
    try {
      await updateCategoryMutation.mutateAsync({
        id: categoryId,
        ...formData,
      });
      handleClose();
    } catch (error) {
      logger.exception("category_update_failed", error);
    }
  };

  // Handle "Update All" - updates category across all shared menus
  const handleUpdateAll = async () => {
    if (updateCategoryMutation.isPending || !savedFormData.current) {
      return;
    }
    try {
      const { availabilityTimes: _unused, ...dataToSend } =
        savedFormData.current;
      await updateCategoryMutation.mutateAsync({
        id: categoryId,
        ...dataToSend,
      });
      handleClose();
      setUpdateSharedCategoryStatus(null);
      setMenusToCopyCategoryTo([]);
    } catch (error) {
      logger.exception("category_update_all_failed", error);
    }
  };

  // Handle "Save as New Category" - clones category for selected menus only
  const handleSaveAsNew = async () => {
    if (cloneAndUpdateCategoryMutation.isPending || !savedFormData.current) {
      return;
    }
    try {
      const { availabilityTimes: _unused, ...dataToSend } =
        savedFormData.current;
      await cloneAndUpdateCategoryMutation.mutateAsync({
        id: categoryId,
        data: {
          ...dataToSend,
          availabilityTimes: savedFormData.current.availabilityTimes.map(
            (slot) => ({
              dayOfTheWeek: slot.dayOfTheWeek,
              startTime: slot.startTime ?? "",
              endTime: slot.endTime ?? "",
            })
          ),
        },
        menuIds: menusToCopyCategoryTo.map((id) =>
          toConvexTableId("menus", id)
        ),
      });
      handleClose();
      setUpdateSharedCategoryStatus(null);
      setMenusToCopyCategoryTo([]);
    } catch (error) {
      logger.exception("category_clone_failed", error);
    }
  };

  if (isPending) {
    return (
      <ModalLoadingDialog
        message="Loading category details..."
        onOpenChange={handleClose}
      />
    );
  }

  if (!category) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Category not found</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Shared Category Confirmation Dialog
  if (updateSharedCategoryStatus) {
    return (
      <SharedCategoryDialog
        category={category}
        cloneAndUpdateCategoryMutation={cloneAndUpdateCategoryMutation}
        menusToCopyCategoryTo={menusToCopyCategoryTo}
        onSaveAsNew={handleSaveAsNew}
        onUpdateAll={handleUpdateAll}
        setMenusToCopyCategoryTo={setMenusToCopyCategoryTo}
        setUpdateSharedCategoryStatus={setUpdateSharedCategoryStatus}
        uniqueLocations={uniqueLocations}
        updateCategoryMutation={updateCategoryMutation}
        updateSharedCategoryStatus={updateSharedCategoryStatus}
      />
    );
  }

  // Main Edit Form
  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent noScrollPrevention>
        <DialogHeader className="items-center space-y-0">
          <DialogTitle>Edit category</DialogTitle>
          <DialogDescription className="body">
            Use categories to group your menu items
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(handleSave)}>
            <div className="flex gap-3">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Category name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <ColorSelector onValueChange={field.onChange}>
                    <div
                      className={twJoin(
                        "grid h-40 w-40 shrink-0 cursor-pointer place-items-center overflow-hidden break-words rounded-2xl p-4 text-center",
                        categoryCardColors[field.value ?? "white"]
                      )}
                    >
                      <div className="flex flex-col space-y-1">
                        <p className="h4 text-foreground">
                          {form.watch("name")}
                        </p>
                        <p className="caption text-primary text-center">
                          Edit display colour
                        </p>
                      </div>
                    </div>
                  </ColorSelector>
                )}
              />
            </div>
            <fieldset>
              <Label asChild>
                <legend className="text-foreground flex items-center gap-1">
                  When is this category available to order?
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="cursor-help">
                        <Info className="size-4" />
                        <span className="sr-only">
                          Hover for additional information
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Restrict or indicate to staff <br />
                          when menu items in this
                          <br />
                          category are available to order
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </legend>
              </Label>
              <FormField
                control={form.control}
                name="matchBusinessHours"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center pt-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="body-sm text-foreground">
                      Match location business hours
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>
            {!form.watch("matchBusinessHours") && (
              <>
                <fieldset>
                  <legend className="pb-3">
                    <Label asChild>
                      <p>Set available hours</p>
                    </Label>
                    <p className="body-sm text-foreground">
                      Indicate when menu items are available in POS.
                    </p>
                  </legend>
                  <AvailabilityTimesFields showLabel={false} />
                </fieldset>
                <fieldset>
                  <Label asChild>
                    <legend>Disable ordering outside of available hours</legend>
                  </Label>
                  <FormField
                    control={form.control}
                    name="isCategoryLockEnabled"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center pt-3">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="body-sm text-foreground">
                          Lock menu items when this category is not available
                        </FormLabel>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </fieldset>
              </>
            )}
            <div className="flex justify-between">
              <Button
                onClick={handleNavigateToDelete}
                size="lg"
                type="button"
                variant="secondary"
              >
                Remove Category
              </Button>
              <Button
                disabled={updateCategoryMutation.isPending}
                size="lg"
                type="submit"
              >
                Save Category
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
